import { Check, Phone } from "lucide-react";
import LandingBookingCard from "./LandingBookingCard";
import { useLandingContent } from "@/lib/landing/LandingContentProvider";
import { pickVariant } from "@/lib/landing/registry";
import { keepTogetherHighlights, phoneHref } from "@/lib/landing/resolve";

interface Props {
  variant: string;
  step: 1 | 2;
  onStepChange: (step: 1 | 2) => void;
  onCall: () => void;
  onBookingHelpCall: () => void;
}
export default function LandingHero({
  variant,
  step,
  onStepChange,
  onCall,
  onBookingHelpCall,
}: Props) {
  const { content, definition } = useLandingContent();
  const headline = pickVariant(content, variant).text;
  const keepTogether = keepTogetherHighlights(definition.defaults).includes(headline.highlight);
  return (
    <section
      className={`landing-hero ${step === 2 ? "landing-hero-booking" : ""}`}
      aria-labelledby="landing-title"
    >
      <div className="landing-container landing-hero-grid">
        <div className="landing-intro">
          <p data-lp="variant.eyebrow" className="landing-eyebrow">{headline.eyebrow}</p>
          <h1
            id="landing-title"
            data-lp-tail="variant.rest"
            className="landing-headline mt-3 font-display font-bold sm:mt-6"
          >
            <span
              data-lp="variant.highlight"
              className={`${keepTogether ? "whitespace-nowrap " : ""}text-landing-headline`}
            >
              {headline.highlight}
            </span>
            {` ${headline.rest}`}
          </h1>
          {step === 1 && (
            <p className="mt-3 max-w-[560px] text-[15.5px] leading-[1.5] sm:mt-6 sm:text-[19px] sm:leading-[1.6] text-landing-on-dark-body">
              <span data-lp="hero.intro" className="hidden sm:inline">
                {content.hero.intro}
              </span>
              <span data-lp="hero.introMobile" className="sm:hidden">
                {content.hero.introMobile}
              </span>
            </p>
          )}
        </div>
        <div className="landing-booking">
          <LandingBookingCard
            variant={variant}
            onStepChange={onStepChange}
            onBookingHelpCall={onBookingHelpCall}
          />
        </div>
        {step === 1 && (
          <div className="landing-details">
            <ul className="space-y-3">
              {content.hero.checklist.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-[16.5px] text-landing-check-text"
                >
                  <span className="mt-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-landing-headline/20">
                    <Check className="h-3.5 w-3.5 text-landing-red-soft" />
                  </span>
                  <span data-lp={`hero.checklist.${i}`}>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-landing-call-border pt-6">
              <span data-lp="hero.callPrompt" className="text-[15px] text-landing-on-dark-muted">
                {content.hero.callPrompt}
              </span>
              <a
                href={phoneHref(content.phone)}
                onClick={onCall}
                data-lp-tel=""
                className="flex items-center gap-3 landing-brand-text font-display text-[22px] font-semibold text-landing-on-dark sm:text-[27px]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-landing-headline">
                  <Phone className="h-4 w-4 text-landing-red-soft" />
                </span>
                <span data-lp="phone" data-lp-prefix="Call ">{`Call ${content.phone}`}</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
